import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import GradingIcon from "@mui/icons-material/GradeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import Popup from "../../../components/Popup";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { createManebSession, fetchManebDashboard } from "../../../utils/manebApi";

const EXAM_TYPES = ["PSLCE", "JCE", "MSCE"];
const y = new Date().getFullYear();
const ACADEMIC_YEARS = [`${y - 1}/${y}`, `${y}/${y + 1}`];

const ManebDashboard = () => {
    const navigate = useNavigate();
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        classId: "",
        examType: "MSCE",
        title: "",
        sessionKind: "Mock",
        academicYear: ACADEMIC_YEARS[1],
    });

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            setData(await fetchManebDashboard(schoolId));
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load MANEB dashboard");
            setShowPopup(true);
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    const examClasses = data?.examClasses || [];
    const sessions = data?.sessions || [];

    const handleCreate = async () => {
        if (!form.title.trim() || !form.classId) {
            setMessage("Select an exam class and enter a session title");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const created = await createManebSession({ ...form, schoolId, title: form.title.trim() });
            setCreateOpen(false);
            setForm((f) => ({ ...f, title: "" }));
            navigate(`/Admin/maneb/session/${created.id || created._id}`);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not create session");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            MANEB national examinations
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 680 }}>
                            PSLCE (Std 8), JCE (Form 2), and MSCE (Form 4) — candidate registration, mock exams,
                            timetable, and certificate tracking aligned to Malawi National Examinations Board returns.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button component={RouterLink} to="/Admin/calendar" variant="outlined" startIcon={<CalendarMonthOutlinedIcon />}>
                            Exam timetable
                        </Button>
                        <Button component={RouterLink} to="/Admin/maneb/certificates" variant="outlined" startIcon={<GradingIcon />}>
                            Certificates
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                            New session
                        </Button>
                    </Stack>
                </Stack>

                {data?.setupRequired ? (
                    <Alert severity="warning">
                        MANEB database tables are not set up on this server. Run migration{" "}
                        <strong>20260603180000_maneb_exams</strong> in Supabase SQL, or use the local backend.
                    </Alert>
                ) : null}

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        EXAM CLASSES
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {data?.summary?.examClassCount ?? 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Std 8 · Form 2 · Form 4 auto-detected
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        SESSIONS
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {data?.summary?.sessionCount ?? 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Mock & official exam cycles
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        CENTRE CODE
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {data?.school?.manebCentreNumber || data?.school?.schoolCode || "Not set"}
                                    </Typography>
                                    <Button component={RouterLink} to="/Admin/profile" size="small" sx={{ mt: 0.5, px: 0 }}>
                                        Set in profile
                                    </Button>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Exam classes
                                </Typography>
                            </Box>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Class</TableCell>
                                        <TableCell>Exam</TableCell>
                                        <TableCell>Students</TableCell>
                                        <TableCell>Form teacher</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {examClasses.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Typography color="text.secondary" sx={{ py: 2 }}>
                                                    No exam class yet. Create Std 8, Form 2, or Form 4 — exam type is set
                                                    automatically.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        examClasses.map((cls) => (
                                            <TableRow key={cls.id || cls._id}>
                                                <TableCell>{cls.sclassName}</TableCell>
                                                <TableCell>
                                                    <Chip label={cls.examType} size="small" color="warning" />
                                                </TableCell>
                                                <TableCell>{cls._count?.students ?? "—"}</TableCell>
                                                <TableCell>{cls.classTeacher?.name || "—"}</TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            setForm((f) => ({
                                                                ...f,
                                                                classId: cls.id || cls._id,
                                                                examType: cls.examType || "MSCE",
                                                                title: `${cls.examType} ${form.academicYear} Mock`,
                                                            }));
                                                            setCreateOpen(true);
                                                        }}
                                                    >
                                                        New session
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </Paper>

                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Exam sessions
                                </Typography>
                            </Box>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Year</TableCell>
                                        <TableCell>Kind</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Open</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sessions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Typography color="text.secondary" sx={{ py: 2 }}>
                                                    Create a mock or official session to register candidates.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sessions.map((s) => (
                                            <TableRow key={s.id || s._id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/Admin/maneb/session/${s.id || s._id}`)}>
                                                <TableCell>{s.title}</TableCell>
                                                <TableCell>
                                                    <Chip label={s.examType} size="small" />
                                                </TableCell>
                                                <TableCell>{s.academicYear}</TableCell>
                                                <TableCell>{s.sessionKind}</TableCell>
                                                <TableCell>{s.status}</TableCell>
                                                <TableCell align="right">
                                                    <Button size="small">Manage</Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </Paper>
                    </>
                )}
            </Stack>

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create MANEB exam session</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Exam class</InputLabel>
                            <Select
                                label="Exam class"
                                value={form.classId}
                                onChange={(e) => {
                                    const cls = examClasses.find((c) => (c.id || c._id) === e.target.value);
                                    setForm((f) => ({
                                        ...f,
                                        classId: e.target.value,
                                        examType: cls?.examType || f.examType,
                                    }));
                                }}
                            >
                                {examClasses.map((c) => (
                                    <MenuItem key={c.id || c._id} value={c.id || c._id}>
                                        {c.sclassName} ({c.examType})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            size="small"
                            label="Session title"
                            value={form.title}
                            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="e.g. 2026 MSCE Mock Examination"
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel>Examination</InputLabel>
                            <Select label="Examination" value={form.examType} onChange={(e) => setForm((f) => ({ ...f, examType: e.target.value }))}>
                                {EXAM_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Session kind</InputLabel>
                            <Select label="Session kind" value={form.sessionKind} onChange={(e) => setForm((f) => ({ ...f, sessionKind: e.target.value }))}>
                                <MenuItem value="Mock">Mock examination</MenuItem>
                                <MenuItem value="Official">Official MANEB session</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Academic year</InputLabel>
                            <Select label="Academic year" value={form.academicYear} onChange={(e) => setForm((f) => ({ ...f, academicYear: e.target.value }))}>
                                {ACADEMIC_YEARS.map((yr) => (
                                    <MenuItem key={yr} value={yr}>
                                        {yr}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={handleCreate}>
                        {saving ? "Creating…" : "Create session"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup showPopup={showPopup} setShowPopup={setShowPopup} title="MANEB">
                <Typography>{message}</Typography>
            </Popup>
        </Container>
    );
};

export default ManebDashboard;
